import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, OnDestroy } from '@angular/core';
import { distinctUntilChanged, map, takeUntil, tap } from 'rxjs/operators';
import { SharedCatalogState } from '@shared/state/catalog/shared-catalog.state';
import { Select, Store } from '@ngxs/store';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { IDestroy, TakeUntilDestroy } from '@utils/auto-unsubscribe';
import { Observable, Subject } from 'rxjs';
import { CatalogItemFieldsVM, CatalogType } from '@shared/models/shared-catalog.state';
import { convertCatalogItemFieldsVMToView } from '@utils/converters';
import { cloneDeep } from 'lodash';
import { FileData, SelectOptionMobile } from '@shared/models/catalog.service';
import { CustomIcons } from '@shared/models/custom-icons';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ImpairedPaginationComponent } from '@impairedShared/impaired-pagination/impaired-pagination/impaired-pagination.component';
import {
  CatalogDynamicColumns,
  CatalogItemsQueryParams,
  CatalogStaticColumns
} from '../../../../../../../data/modules/catalog-items-data/models/catalog-item-component';
import { CatalogItemsState } from '../../../../../../../data/modules/catalog-items-data/store/catalog-items-state';
import {
  ChangedCatalogItemsFilter,
  ChangedCatalogItemsPaginator,
  GetCatalogItemAllCount,
  InitCatalogItemTable,
  SetCatalogItemCriteriaChanged
} from '../../../../../../../data/modules/catalog-items-data/store/catalog-items.actions';
import { CatalogItemValue } from '../../../../../../../data/models/catalog';
import { VersionForVisuallyImpairedRoutes } from '../../../../../../models/routes';
import { ImpairedCatalogItemsRoutes } from '../../../models/routes';
import { ImpairedSorting } from '../../../../models/data-table';
import { ConnectedPosition } from "@angular/cdk/overlay";
import { MetaDataService } from '@core/meta-data/meta-data.service';

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@TakeUntilDestroy
export class MainComponent implements OnInit, IDestroy, OnDestroy {
  private _changeFilter = false;
  private _pageIndex = 1;
  private _pageSize = 10;
  private _currentCatalogId: number;
  @ViewChild('paginator', { static: true }) public paginator: ImpairedPaginationComponent;
  public isDynamic: boolean;
  public componentDestroy: () => Subject<any>;
  public allColumns: CatalogDynamicColumns[];
  public showSelectedItemInfo: boolean;
  public staticColumns: CatalogStaticColumns[];
  public tableColumnsStyle = '';
  public dynamicTableColumnsStyle = '';
  public dynamicGridColumn = '';
  public pcColumnsFilter: SelectOptionMobile[] = [];
  public currentData: CatalogItemValue[];
  public activeColumnSetting = false;
  public icons = CustomIcons;
  public formObject: UntypedFormGroup;
  public allCatalogCount$ = this.store.select(CatalogItemsState.getCatalogItemCount).pipe(map(filterFn => filterFn(this._currentCatalogId)));
  public columnSettingOverlayPosition: ConnectedPosition[] = [
    {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top'
    }
  ];
  public sorting: ImpairedSorting[] = [];
  @Select(CatalogItemsState.getFileData) public fileData$: Observable<FileData[]>;

  constructor(
    private store: Store,
    private activeRoute: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private metaDataService: MetaDataService,
  ) {}

  private setColumn(res: CatalogItemFieldsVM[]): void {
    if (res.length === 0) {
      return;
    }
    const allColumns = convertCatalogItemFieldsVMToView([...res])
      .sort((a, b) => (a.order > b.order ? 1 : -1))
      .filter(x => x.type !== CatalogType.Catalog);
    let firstStaticColumn = allColumns.find(x => x.systemField === 'name');
    if (!firstStaticColumn) {
      firstStaticColumn = allColumns.find(x => x.systemField === 'name' || x.show) ?? allColumns[0];
    }

    this.staticColumns = [
      {
        name: 'numberClientOpod',
        title: '№',
        type: CatalogType.String,
        order: -1,
        catalogId: 1,
        calculationWidth: 20
      },
      firstStaticColumn
    ];

    const dynamicColumns = allColumns.filter(x => x.name !== firstStaticColumn?.name);

    this.pcColumnsFilter = dynamicColumns.map(column => ({
      title: column.title,
      isSelected: column.show,
      name: column.name,
      isNumber: column.type === CatalogType.Number || column.type === CatalogType.Integer || column.type === CatalogType.Numeric,
      isLink: column.type === CatalogType.LINK,
      catalogId: column.catalogId,
      width: column.name.length,
      calculationWidth: column.name.length,
      type: column.type,
    }));

    let formObject = {};

    [...this.staticColumns, ...this.pcColumnsFilter].forEach(x => {
      formObject = { ...formObject, ...{ [x.name]: new UntypedFormControl('') } };
    });

    this.formObject = new UntypedFormGroup(formObject);
    this.changeTableColumnsStyle(this.pcColumnsFilter.filter(x => x.isSelected).length);

    this.allColumns = cloneDeep(allColumns);
    this.cdr.detectChanges();
  }

  private initTable(isInitial = false): void {
    const queryParams = this.activeRoute.snapshot.queryParams;
    const pageSize = isInitial ? +(queryParams['pageSize'] ?? 10) : (this.paginator.pageSize ?? 10);
    const pageIndex = isInitial ? +(queryParams['pageIndex'] ?? 0) : (this.paginator.pageIndex ?? 1) - 1;
    this.store
      .dispatch(new InitCatalogItemTable(
        this.isDynamic,
        this.getSorting(),
        +(this.activeRoute.parent?.parent?.snapshot.params['id'] || 0),
        { pageSize, pageIndex },
        true
      ))
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe(() => {
        if (!this.allColumns || this.allColumns.length === 0) {
          const columns = this.store.selectSnapshot(SharedCatalogState.catalogInfo)?.fields;
          if (columns) {
            this.setColumn(columns);
          }
        }
        const count = this.store.selectSnapshot(CatalogItemsState.getCatalogItemsData).call(this, this._currentCatalogId)?.length;
        if (count === 0) {
          this.showSelectedItemInfo = false;
        }
        this.cdr.detectChanges();
      });
  }

  private changeTableColumnsStyle(columnLength: number): void {
    this.tableColumnsStyle = '70px 240px';
    if (columnLength !== 0) {
      this.dynamicTableColumnsStyle = `repeat(${columnLength}, minmax(1fr,220px))`;
      this.tableColumnsStyle += ` ${this.dynamicTableColumnsStyle}`;
      this.dynamicGridColumn = `4/${columnLength + 3}`;
    }
    this.cdr.detectChanges();
  }

  private changeFilter(filter: any): void {
    let criteria = '';
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    Object.keys(filter)
      .filter(key => filter[key])
      .forEach(key => {
        const currentFilter = this.allColumns.find(x => x.name === key);
        let searchString = filter[key];
        if (currentFilter) {
          if (criteria) {
            criteria += ' and ';
          }
          switch (currentFilter.type) {
            case CatalogType.String:
            case CatalogType.Text:
              searchString = ` ilike '%${searchString}%'`;
              break;
            case CatalogType.Dictionary:
              searchString = ` = '${searchString}'`;
              break;
            case CatalogType.Timestamp:
              // eslint-disable-next-line
              searchString = ` = \'${searchString}\'`;
              break;
            default:
              searchString = ` = ${searchString}`;
              break;
          }

          criteria += `${currentFilter.name}${searchString}`;
        }
      });
    this._changeFilter = true;
    this.paginator.pageIndex = 1;
    this._pageIndex = 1;
    this.store.dispatch(new ChangedCatalogItemsPaginator(0, this.paginator.pageSize));
    this.store.dispatch(new ChangedCatalogItemsFilter(criteria));
    this.initTable();
  }

  private changeData(data: CatalogItemValue[]): void {
    this.currentData = cloneDeep(data)
      .splice((this.paginator.pageIndex! - 1) * this.paginator.pageSize!, this.paginator.pageSize!);

    if (this._changeFilter || !this.paginator?.pageSize) {
      this._changeFilter = false;
    }
    this.cdr.detectChanges();
  }

  private getSorting(): any | null {
    return this.sorting.reduce((sorting, item) => {
      return { ...sorting, [item.type]: item.value };
    }, {});
  }

  private catalogInit(catalogId: number) {
    this.allColumns = [];
    this._pageSize = this.store.selectSnapshot(CatalogItemsState.getCatalogItemPageSize).call(this, catalogId);
    this._pageIndex = this.store.selectSnapshot(CatalogItemsState.getCatalogItemPageIndex).call(this, catalogId) + 1;
    this.paginator.pageIndex = this._pageIndex;
    this.paginator.pageSize = this._pageSize;

    this._currentCatalogId = catalogId;
    this.isDynamic = (this.activeRoute.snapshot.queryParams as CatalogItemsQueryParams)?.isDynamic === 'true';

    if (!this.isDynamic) {
      this.store.dispatch(new GetCatalogItemAllCount(this._currentCatalogId));
    }
    this.initTable(true);

    this.store
      .select(CatalogItemsState.getCatalogItemsData)
      .pipe(
        map(filterFn => filterFn(this._currentCatalogId)),
        distinctUntilChanged(),
        tap(data => {
          if (data) {
            this.changeData(data);
          }
        }),
        takeUntil(this.componentDestroy())
      )
      .subscribe();
  }

  public ngOnInit(): void {
    this.router.events.pipe(takeUntil(this.componentDestroy())).subscribe(event => {
      if (event instanceof NavigationEnd) {
        const newCatalogId = +event.url.split('/')[3];
        if (newCatalogId && newCatalogId !== this._currentCatalogId) {
          this.catalogInit(newCatalogId);
        }
      }
    });

    const catalogInfo = this.store.selectSnapshot(SharedCatalogState.catalogInfo);
    this.metaDataService.updateTitleAfterDataLoading(
      '/visually-impaired/dataset/id/datasetItems',
      catalogInfo?.nameVariants?.[0]?.fullName,
      catalogInfo?.accountingObject,
    );

    const catalogId = this.activeRoute.parent?.parent?.snapshot.paramMap.get('id');

    if (!catalogId || isNaN(+catalogId)) {
      this.router.navigate([`/visually-impaired/${VersionForVisuallyImpairedRoutes.DataSet}`]);
      return;
    }
    this.catalogInit(+catalogId);
  }

  ngOnDestroy(): void {
    this.store.dispatch(new ChangedCatalogItemsFilter(''));
    this.paginator.pageIndex = 1;
    this.paginator.pageSize = 10;
  }

  public changeCriteria(): void {
    this.changeFilter(this.formObject.value);
  }

  public getStyles(): any {
    return { color: 'red' };
  }

  public changeColumnFilter($event: boolean[]): void {
    $event.forEach((isSelected, index) => {
      this.pcColumnsFilter[index].isSelected = isSelected;
    });
    this.cdr.detectChanges();
  }

  public paginatorChanged($event: { pageIndex: number; pageSize: number }): void {
    this._pageIndex = $event.pageIndex;
    if ($event.pageSize === -1) {
      this._pageSize = this.paginator.pageSize;
    } else {
      this._pageSize = $event.pageSize;
    }
    this.store.dispatch(new ChangedCatalogItemsPaginator(this._pageIndex - 1, this._pageSize));
    this.initTable();
  }

  public goItem(data: CatalogItemValue): void {
    this.router.navigate(
      [
        `/visually-impaired/${VersionForVisuallyImpairedRoutes.DataSet}`,
        this._currentCatalogId,
        ImpairedCatalogItemsRoutes.Item,
        data.global_id
      ],
      {
        queryParams: { isDynamic: this.isDynamic }
      }
    );
  }

  getColumnValue(
    catalogItem: CatalogItemValue,
    columnName: string,
    columnType?: string,
    allFilesData: FileData[] = []
  ) {
    const cellValue = catalogItem ? catalogItem[columnName] : null;

    if (!cellValue) {
      return '';
    }

    if (columnType === 'FILE') {
      return cellValue
        ?.split(';')
        .map((fileId: string) => allFilesData.find(
          file => file.id === fileId)?.fileName
        )
        .join('; ');
    }

    if (Array.isArray(cellValue)) {
      return cellValue
        .map(val => {
          if (typeof val === 'string') { return val; }
          return val.value;
        })
        .join('; ');
    }

    if (typeof cellValue === 'object') {
      return cellValue.value;
    }

    return cellValue;
  }

  isSortDescending(columnName: string) {
    return this.sorting
      .find(item => item.type === columnName)
      ?.value === 'DESC';
  }

  isSortActive(columnName: string) {
    return this.sorting.some(item => item.type === columnName);
  }

  setSorting(columnName: string, columnType: string) {
    if (!this.isSortAvailable(columnType as CatalogType)) {
      return;
    }

    const hasColumnSorting = this.isSortActive(columnName);
    if (hasColumnSorting) {
      this.sorting = this.sorting.map(item => ({
        type: item.type,
        value: columnName === item.type
          ? (item.value === 'DESC' ? 'ASC' : 'DESC')
          : item.value,
      }));
    } else {
      this.sorting = [...this.sorting, { type: columnName, value: 'ASC' }];
    }
    this.store.dispatch(new SetCatalogItemCriteriaChanged());
    this.initTable();
  }

  resetSorting(): void {
    this.sorting = [];
    this.store.dispatch(new SetCatalogItemCriteriaChanged());
    this.initTable();
  }

  getSortCounter(columnName: string) {
    const index = this.sorting.findIndex(item => item.type === columnName);
    return index > -1 ? index + 1 : null;
  }

  isSortAvailable(type: CatalogType) {
    const acceptedTypes = [
      CatalogType.String,
      CatalogType.Number,
      CatalogType.Integer,
      CatalogType.Dictionary,
      CatalogType.Flag,
    ];
    return acceptedTypes.includes(type);
  }
}
